// RentalTermPage.tsx
import React, { FC, useEffect, useState, useCallback } from 'react'
import { Form, Formik, FormikProps } from 'formik'
import { ConfirmModal } from '../../../../shared/components/ConfirmModal'
import {
  DAY_CALC_TYPE,
  IRentalTermsModel,
  initialRentalTerms,
  validationRentalTerms,
} from './RentalTermModel'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { showErrorToast, showSuccessToast } from '../../../../shared/components/ToastUtils'
import { createRentalTerm, deleteRentalTerm, loadRentalTerms, updateRentalTerm } from './Api'
import FormTabHeader, { TabOption } from '../../../../shared/components/FormTabHeader'
import TextInput from '../../../../shared/components/TextInput'
import SelectInput from '../../../../shared/components/SelectInput'
import { RentalTermTable } from './RentalTermTable'
import { branches } from '../../../../shared/models/SharedApiLookups'

type Option = { value: string; label: string }

const PAGE_SIZE = 5

const RentalTermPage: FC = () => {
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [rentalTerms, setRentalTerms] = useState<IRentalTermsModel[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [tab, setTab] = useState<'form' | 'list'>('form')
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [sortBy, setSortBy] = useState<string>('')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [error, setError] = useState<string | null>(null)

  const tabOptions: TabOption<'form' | 'list'>[] = [
    { key: 'form', title: 'Kiralama Şartları', icon: 'bi bi-geo-alt' },
    { key: 'list', title: 'Kiralama Şartları Listesi', icon: 'bi bi-list' },
  ]

  // Listeyi çek
  const fetchRentalTerms = useCallback(
    async (page: number = 0, sort?: string, order?: 'asc' | 'desc') => {
      setLoading(true)
      setError(null)
      try {
        const response = await loadRentalTerms(page, PAGE_SIZE, sort, order)
        if (response.data?.hasError) {
          throw new Error(response.data.message || 'Failed to fetch rental terms')
        }

        const content = response.data?.payload?.content ?? []

        // Backend'ten branch objesi gelebilir; form için branchId:string'e normalize edelim
        const dataWithIndex: IRentalTermsModel[] = content.map((item: any, idx: number) => ({
          id: item.id ?? '',
          branchId: (item.branch?.id ?? item.branchId ?? null) as string | null,
          branchName: item.branch?.branchName ?? item.branchName ?? '', // tablo için name
          minimumDays: item.minimumDays ?? 0,
          dayCalculationType: item.dayCalculationType ?? '',
          startDate: item.startDate ?? '',
          endDate: item.endDate ?? '',
          // tablo için index (tablo bileşenin bunu kullanıp kullanmadığına göre opsiyonel)
          index: idx + 1 + page * PAGE_SIZE,
        }))

        setRentalTerms(dataWithIndex)
        setTotalPages(response.data?.payload?.totalPages ?? 0)
      } catch (err: any) {
        setError(err?.message ?? 'Unknown error occurred')
      } finally {
        setLoading(false)
      }
    },
    []
  )

  useEffect(() => {
    if (tab === 'list') {
      fetchRentalTerms(0)
    }
  }, [tab, fetchRentalTerms])

  // Şube seçenekleri (value:string id, label:string ad)
  const [branchOptions, setBranchOptions] = useState<readonly Option[]>([])

  useEffect(() => {
    const fetchBranchesData = async () => {
      try {
        const data = await branches() // [{value: id, label: branchName}]
        setBranchOptions(data)
      } catch (error) {
        console.error('Şubeler alınırken hata oluştu:', error)
      }
    }
    fetchBranchesData()
  }, [])

const handleSave = async (values: IRentalTermsModel, resetForm: () => void) => {
  setLoading(true)
  try {
    const payload: any = {
      ...values,
      branch: values.branchId ? { id: values.branchId } : null,
    }

    if (selectedId) {
      await updateRentalTerm({ ...payload, id: selectedId })
      showSuccessToast('Kiralama Şartı başarıyla güncellendi')
    } else {
      await createRentalTerm(payload)
      showSuccessToast('Kiralama Şartı başarıyla oluşturuldu')
    }

    setCurrentPage(0)
    await fetchRentalTerms(0)
    resetForm()
    setSelectedId(null)
    setTab('list')
  } catch (err: any) {
    console.error('Kiralama şartı kaydedilirken hata:', err)

    let message = 'Bir hata oluştu'

    // Axios response varsa
    if (err.response && err.response.data) {
      const data = err.response.data
      // Backend duplicate key hatası
      if (
        err.response.status === 500 &&
        data?.message?.toLowerCase().includes('duplicate key')
      ) {
        message = `Sunucudan hata alındı: ${err.response.statusText || err.response.status}`
      } else if (data?.message) {
        message = data.message
      } else {
        message = 'Bu şube için zaten bir kiralama şartı mevcut.'
      }
    } else if (err.message) {
      message = err.message
    }

    showErrorToast(message)
  } finally {
    setLoading(false)
  }
}




  const handleEdit = useCallback(
    (rentalTerm: IRentalTermsModel, formik: FormikProps<IRentalTermsModel>) => {
      // Eğer liste öğesi branch objesiyle geldiyse normalize etmiştik; burada direkt set edebiliriz
      formik.setValues({
        ...rentalTerm,
        branchId: rentalTerm.branchId ?? null,
      })
      setSelectedId(rentalTerm.id)
      setTab('form')
    },
    []
  )

  const handleDeleteClick = useCallback((rentalTerm: IRentalTermsModel) => {
    setSelectedId(rentalTerm.id)
    setShowModal(true)
  }, [])

  const handleDelete = async () => {
    if (!selectedId) return
    setLoading(true)
    try {
      await deleteRentalTerm(selectedId)
      showSuccessToast('Kiralama Şartı başarıyla silindi')
      await fetchRentalTerms(currentPage)
    } catch (err) {
      console.error(err)
      showErrorToast('Kiralama şartı silinirken bir hata oluştu')
    } finally {
      setLoading(false)
      setShowModal(false)
      setSelectedId(null)
    }
  }

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage >= 0 && newPage < totalPages) {
        setCurrentPage(newPage)
        fetchRentalTerms(newPage, sortBy, sortOrder)
      }
    },
    [totalPages, sortBy, sortOrder, fetchRentalTerms]
  )

  const handleSort = useCallback(
  (column: string, order: 'asc' | 'desc') => {
    setSortBy(column)
    setSortOrder(order)

    // frontend sıralaması
    setRentalTerms((prev) => {
      const sorted = [...prev].sort((a, b) => {
        let aValue: any = a[column as keyof IRentalTermsModel]
        let bValue: any = b[column as keyof IRentalTermsModel]

        // branchId kolonunu branchName ile sırala
        if (column === 'branchId') {
          aValue = a.branchName?.toLowerCase() || ''
          bValue = b.branchName?.toLowerCase() || ''
        }

        // startDate kolonunu tarih olarak sırala
        if (column === 'startDate') {
          aValue = new Date(a.startDate).getTime()
          bValue = new Date(b.startDate).getTime()
        }

        if (aValue < bValue) return order === 'asc' ? -1 : 1
        if (aValue > bValue) return order === 'asc' ? 1 : -1
        return 0
      })
      return sorted
    })
  },
  []
)


  return (
    <>
      <Formik
        initialValues={initialRentalTerms}
        validationSchema={validationRentalTerms}
        enableReinitialize
        onSubmit={(values, { resetForm }) => handleSave(values, resetForm)}
      >
        {(formik) => {
          const handleReset = () => {
            formik.resetForm()
            setSelectedId(null)
            setTab('form')
          }

          return (
            <>
              <FormTabHeader
                formId="rentalTermForm"
                formikSubmit={formik.handleSubmit}
                formikReset={handleReset}
                loading={loading}
                tab={tab}
                setTab={setTab as unknown as (tab: string) => void} // tip uyuşmazlığı için cast
                setSelectedId={setSelectedId}
                tabs={tabOptions}
                onExportExcel={() => {}} // prop zorunluysa boş fonksiyon
              />
              <div className="card mb-5">
                {tab === 'form' && (
                  <Form id="rentalTermForm" className="form card-body">
                    <div className="row mb-6">
                      <SelectInput
                        name="branchId" // formda string tutulur
                        label="Şube"
                        wrapperClassName="col-md-6"
                        options={branchOptions} // [{value: id, label: name}]
                      />
                      <TextInput
                        name="startDate"
                        type="date"
                        label="Başlangıç Tarihi"
                        wrapperClassName="col-md-6"
                      />
                    </div>

                    <div className="row mb-6">
                      <TextInput
                        name="minimumDays"
                        type="number"
                        label="Minimum Gün"
                        wrapperClassName="col-md-6"
                      />
                      <TextInput
                        name="endDate"
                        type="date"
                        label="Bitiş Tarihi"
                        wrapperClassName="col-md-6"
                      />
                    </div>

                    <div className="row mb-6">
                      <SelectInput
                        name="dayCalculationType"
                        label="Gün Hesaplama Tipi"
                        options={DAY_CALC_TYPE}
                        wrapperClassName="col-md-6"
                      />
                    </div>
                  </Form>
                )}
              </div>

              {tab === 'list' && (
                <div className="card">
                  <div className="card-body pt-0">
                    <RentalTermTable
                      data={rentalTerms}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                      onEdit={(rentalTerm) => handleEdit(rentalTerm, formik)}
                      onDelete={handleDeleteClick}
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                      loading={loading}
                    />
                  </div>
                </div>
              )}
            </>
          )
        }}
      </Formik>

      <ConfirmModal
        show={showModal}
        header="Silme Onayı"
        text="Bu kiralama şartını silmek istediğinize emin misiniz?"
        handleClose={() => {
          setShowModal(false)
          setSelectedId(null)
        }}
        applyOnClick={handleDelete}
      />

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  )
}

export { RentalTermPage }

